import { Fragment } from 'react'

// Minimal markdown renderer for chat messages. Deliberately small: no full
// CommonMark, no tables, no code blocks — just what assistant replies actually
// emit. Adding react-markdown would bring in remark/mdast (~30KB gz) for a
// surface that today produces **bold**, *italic*, `code`, [link](url),
// bullet lists (- ), numbered lists (1. ) and paragraph breaks.

const INLINE_PATTERN = /(\*\*[^*\n]+\*\*|\*[^*\n]+\*|_[^_\n]+_|`[^`\n]+`|\[[^\]\n]+\]\([^)\n]+\))/g

function renderInline(text) {
  if (!text) return null
  const parts = text.split(INLINE_PATTERN)
  return parts.map((part, i) => {
    if (!part) return null
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i}>{part.slice(2, -2)}</strong>
    }
    if ((part.startsWith('*') && part.endsWith('*')) || (part.startsWith('_') && part.endsWith('_'))) {
      return <em key={i}>{part.slice(1, -1)}</em>
    }
    if (part.startsWith('`') && part.endsWith('`')) {
      return <code key={i} className="ws-md-code">{part.slice(1, -1)}</code>
    }
    const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/)
    if (linkMatch) {
      return (
        <a key={i} href={linkMatch[2]} target="_blank" rel="noopener noreferrer" className="ws-md-link">
          {linkMatch[1]}
        </a>
      )
    }
    return <Fragment key={i}>{part}</Fragment>
  })
}

// Group consecutive list lines into <ul>/<ol>; treat blank lines as paragraph
// breaks. A trailing block of bullets is the most common case in Marq's
// responses ("Here's what's still missing: - X - Y - Z").
export function renderMarkdown(text) {
  if (!text) return null
  const lines = String(text).replace(/\r\n/g, '\n').split('\n')

  const blocks = []
  let buffer = []
  let listType = null

  const flushParagraph = () => {
    if (buffer.length === 0) return
    blocks.push({ type: 'p', lines: buffer })
    buffer = []
  }
  const flushList = () => {
    if (buffer.length === 0) return
    blocks.push({ type: listType, lines: buffer })
    buffer = []
    listType = null
  }

  for (const raw of lines) {
    const line = raw
    const isBullet = /^\s*[-*]\s+/.test(line)
    const isNumbered = /^\s*\d+\.\s+/.test(line)
    const isList = isBullet || isNumbered
    const isBlank = line.trim() === ''

    if (isBlank) {
      if (listType) flushList()
      else flushParagraph()
      continue
    }
    if (isList) {
      const wantType = isBullet ? 'ul' : 'ol'
      if (listType && listType !== wantType) flushList()
      if (!listType) {
        flushParagraph()
        listType = wantType
      }
      buffer.push(line.replace(/^\s*[-*]\s+/, '').replace(/^\s*\d+\.\s+/, ''))
      continue
    }
    if (listType) flushList()
    buffer.push(line)
  }
  if (listType) flushList()
  else flushParagraph()

  return blocks.map((b, i) => {
    if (b.type === 'p') {
      return (
        <p key={i} className="ws-md-p">
          {b.lines.map((l, j) => (
            <Fragment key={j}>
              {j > 0 && <br />}
              {renderInline(l)}
            </Fragment>
          ))}
        </p>
      )
    }
    const Tag = b.type
    return (
      <Tag key={i} className="ws-md-list">
        {b.lines.map((l, j) => (
          <li key={j}>{renderInline(l)}</li>
        ))}
      </Tag>
    )
  })
}
